// Team Activity Feed — Story 92
// Real-time chronological list of team activities via Firestore onSnapshot.
// Props:
//   teamId    — the team to subscribe to
//   projectID — when provided, filters to only that project's activities

import React, { useEffect, useState } from "react";
import { collection, query, orderBy, limit, onSnapshot } from "firebase/firestore";
import { db } from "../../../config/firebase";
import { TeamActivity, TeamActivityType } from "../../../config/teamActivity";
import { STEP_CONFIGS } from "../dashboards/steps/stepConfig";

// Step title lookup built from the single source of truth
const STEP_TITLE_MAP: Record<number, string> = {};
STEP_CONFIGS.forEach((s) => {
  STEP_TITLE_MAP[s.stepNumber] = s.pageTitle;
});

// ─── Helpers ──────────────────────────────────────────────────────────────────

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return "yesterday";
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

function getInitials(email: string): string {
  if (!email) return "??";
  const name = email.split("@")[0] ?? "";
  return name.slice(0, 2).toUpperCase();
}

function getActorName(email: string): string {
  if (!email) return "Someone";
  return email.split("@")[0] ?? email;
}

// Deterministic colour per actor based on first char of UID
const AVATAR_PALETTES: Array<{ bg: string; text: string }> = [
  { bg: "#DBEAFE", text: "#1D4ED8" }, // blue
  { bg: "#EDE9FE", text: "#6D28D9" }, // purple
  { bg: "#D1FAE5", text: "#065F46" }, // green
  { bg: "#FCE7F3", text: "#BE185D" }, // pink
  { bg: "#FEF3C7", text: "#92400E" }, // amber
];

function getAvatarPalette(uid: string): { bg: string; text: string } {
  const idx = (uid.charCodeAt(0) ?? 0) % AVATAR_PALETTES.length;
  return AVATAR_PALETTES[idx]!;
}

// ─── Activity type config ──────────────────────────────────────────────────────

const TYPE_CONFIG: Record<TeamActivityType, { icon: string; color: string }> = {
  step_completed:  { icon: "bi-check-circle-fill", color: "#10B981" },
  step_edited:     { icon: "bi-pencil-fill",        color: "#3B82F6" },
  ai_analysis_run: { icon: "bi-cpu-fill",           color: "#8B5CF6" },
  step_assigned:   { icon: "bi-person-check-fill",  color: "#F59E0B" },
  member_joined:   { icon: "bi-person-plus-fill",   color: "#3B82F6" },
  project_shared:  { icon: "bi-share-fill",         color: "#8B5CF6" },
};

function buildActivityText(a: TeamActivity): string {
  switch (a.type) {
    case "step_completed":
    case "step_edited": {
      const title = a.stepTitle ?? (a.stepNumber ? STEP_TITLE_MAP[a.stepNumber] : null);
      const verb = a.type === "step_completed" ? "completed" : "edited";
      return `${verb} Step ${a.stepNumber ?? ""}${title ? ` — ${title}` : ""}`;
    }
    case "ai_analysis_run":
      return "ran AI analysis";
    case "step_assigned": {
      const title = a.stepTitle ?? (a.stepNumber ? STEP_TITLE_MAP[a.stepNumber] : null);
      const target = a.metadata?.assignedToEmail as string | undefined;
      return `assigned Step ${a.stepNumber ?? ""}${title ? ` — ${title}` : ""}${target ? ` to ${target}` : ""}`;
    }
    case "member_joined":
      return "joined the team";
    case "project_shared":
      return `shared ${a.projectTitle ? `"${a.projectTitle}"` : "a project"} with the team`;
    default:
      return "performed an action";
  }
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function ActivityRow({
  activity,
  isLast,
}: {
  activity: TeamActivity;
  isLast: boolean;
}) {
  const tc = TYPE_CONFIG[activity.type] ?? TYPE_CONFIG.step_edited;
  const palette = getAvatarPalette(activity.actorUID);
  const initials = getInitials(activity.actorEmail);
  const actorName = getActorName(activity.actorEmail);
  const text = buildActivityText(activity);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
        padding: "10px 20px",
        borderBottom: isLast ? "none" : "1px solid #F1F5F9",
      }}
    >
      {/* Avatar */}
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          background: palette.bg,
          color: palette.text,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 11,
          fontWeight: 700,
          flexShrink: 0,
          lineHeight: 1,
        }}
      >
        {initials}
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, color: "#0F172A", lineHeight: 1.5 }}>
          <span style={{ fontWeight: 600 }}>{actorName}</span>
          {" "}
          <span style={{ color: "#475569" }}>{text}</span>
        </div>
        {/* Project name for non-step activities when no step info */}
        {activity.projectTitle && !activity.stepNumber && activity.type !== "project_shared" && (
          <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 2 }}>
            {activity.projectTitle}
          </div>
        )}
      </div>

      {/* Type icon + timestamp */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          gap: 4,
          flexShrink: 0,
        }}
      >
        <i className={`bi ${tc.icon}`} style={{ fontSize: 13, color: tc.color }} />
        <span style={{ fontSize: 11, color: "#94A3B8", whiteSpace: "nowrap" }}>
          {timeAgo(activity.timestamp)}
        </span>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const PAGE_SIZE = 10;
const MAX_VISIBLE = 50;

interface Props {
  teamId: string;
  projectID?: string; // when provided, shows only this project's activities
}

export function TeamActivityFeed({ teamId, projectID }: Props) {
  const [activities, setActivities] = useState<TeamActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  useEffect(() => {
    if (!teamId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setVisibleCount(PAGE_SIZE); // reset pagination on teamId change

    const activityRef = collection(db, "teams", teamId, "activity");
    const q = query(activityRef, orderBy("timestamp", "desc"), limit(MAX_VISIBLE));

    const unsub = onSnapshot(
      q,
      (snap) => {
        const items: TeamActivity[] = snap.docs.map((d) => ({
          id: d.id,
          ...(d.data() as Omit<TeamActivity, "id">),
        }));
        setActivities(items);
        setLoading(false);
      },
      () => {
        // onSnapshot error — non-critical, just stop loading
        setLoading(false);
      }
    );

    return () => unsub();
  }, [teamId]);

  // Client-side project filter (avoids needing a Firestore composite index)
  const filtered = projectID
    ? activities.filter((a) => a.projectID === projectID)
    : activities;

  const visible = filtered.slice(0, visibleCount);
  const hasMore = filtered.length > visibleCount;

  const cardStyle: React.CSSProperties = {
    background: "#FFFFFF",
    border: "1px solid #E2E8F0",
    borderRadius: 12,
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
    marginBottom: 16,
    overflow: "hidden",
  };

  return (
    <div style={cardStyle}>
      {/* Header */}
      <div
        style={{
          padding: "14px 20px",
          borderBottom: "1px solid #F1F5F9",
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        <i className="bi bi-activity" style={{ fontSize: 14, color: "#475569" }} />
        <span style={{ fontSize: 13, fontWeight: 600, color: "#475569" }}>
          {projectID ? "Project Activity" : "Team Activity"}
        </span>
        {!loading && filtered.length > 0 && (
          <span
            style={{
              marginLeft: "auto",
              fontSize: 11,
              fontWeight: 500,
              color: "#94A3B8",
            }}
          >
            {filtered.length} {filtered.length === 1 ? "event" : "events"}
          </span>
        )}
      </div>

      {/* Loading */}
      {loading && (
        <div style={{ padding: "24px 20px", textAlign: "center" }}>
          <span
            className="spinner-border spinner-border-sm text-muted"
            role="status"
            aria-label="Loading activity"
          />
        </div>
      )}

      {/* Empty state */}
      {!loading && visible.length === 0 && (
        <div style={{ padding: "28px 20px", textAlign: "center" }}>
          <i
            className="bi bi-activity"
            style={{ fontSize: 24, color: "#CBD5E1", display: "block", marginBottom: 8 }}
          />
          <p style={{ fontSize: 13, color: "#94A3B8", margin: 0 }}>
            {projectID
              ? "No activity on this project yet."
              : "No team activity yet. Activity appears here when team members work on steps."}
          </p>
        </div>
      )}

      {/* Activity list */}
      {!loading && visible.length > 0 && (
        <>
          <div>
            {visible.map((activity, idx) => (
              <ActivityRow
                key={activity.id ?? `${activity.timestamp}-${idx}`}
                activity={activity}
                isLast={idx === visible.length - 1}
              />
            ))}
          </div>

          {/* Load more */}
          {hasMore && (
            <div
              style={{
                padding: "12px 20px",
                borderTop: "1px solid #F1F5F9",
              }}
            >
              <button
                type="button"
                onClick={() => setVisibleCount((v) => Math.min(v + 20, MAX_VISIBLE))}
                style={{
                  background: "none",
                  border: "none",
                  color: "#3B82F6",
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: "pointer",
                  padding: 0,
                  textDecoration: "none",
                }}
              >
                Load more ({Math.min(filtered.length - visibleCount, 20)} more)
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
